import type { Me } from './types'

/**
 * Pick the best post-login landing route for this principal.
 *
 * The previous behaviour hard-coded `/alerts` as the fallback, but
 * not every role holds `alerts:read` — a senior analyst with
 * `cases:read` but no alerts permission would log in and land on
 * a 403, with the only escape being to manually type a different
 * URL. This helper walks a preference ladder and returns the first
 * route the user actually has permission to view.
 *
 * Order rationale:
 *   1. /alerts          — the analyst hot-path; most operators want this
 *   2. /cases           — fallback for analysts without alerts:read
 *   3. /dashboard       — exec / compliance audience
 *   4. /portfolio       — exec entry when /dashboard is gated tighter
 *   5. /customers       — last resort for read-only customer roles
 *   6. /transactions    — for api_user-style roles
 *   7. /settings        — every authenticated user can open this; the
 *                         ultimate "no other surface fits" landing
 *
 * Super-admin (system:admin) always lands on /alerts because they
 * implicitly satisfy every gate; the first route in the ladder
 * wins.
 */

interface LandingRule {
  to: string
  // The permissions required to land here. Match `useCanAny`
  // semantics: any of these in `me.permissions` is enough.
  any: readonly string[]
}

const LANDING_LADDER: readonly LandingRule[] = [
  // Inbox is the canonical analyst workspace — unified view of
  // every actionable item. Lands first whenever the user holds
  // inbox:read.
  { to: '/inbox',        any: ['inbox:read'] },
  { to: '/alerts',       any: ['alerts:read'] },
  { to: '/cases',        any: ['cases:read'] },
  // Compliance officers don't carry alerts/cases write but do hold
  // audit:read + sars:read; route them to the Compliance Dashboard
  // by preference instead of the generic /dashboard.
  { to: '/compliance',   any: ['sars:read'] },
  { to: '/dashboard',    any: ['audit:read'] },
  { to: '/portfolio',    any: ['audit:read'] },
  { to: '/customers',    any: ['customers:read'] },
  { to: '/transactions', any: ['transactions:read'] },
  { to: '/settings',     any: [] }, // sentinel — always matches
]

/**
 * RoutePermission maps a path prefix to the permissions required to
 * access it. Used by `validateNext` to reject a `?next=` deep-link
 * the user can't actually open. Keep in sync with the
 * RequirePermissionRoute guards in router.tsx — pages not listed
 * here are assumed open to any authenticated user (e.g. /settings).
 */
const PATH_GUARDS: ReadonlyArray<{ prefix: string; any: readonly string[] }> = [
  { prefix: '/inbox',                    any: ['inbox:read'] },
  { prefix: '/alerts',                   any: ['alerts:read'] },
  { prefix: '/cases',                    any: ['cases:read'] },
  { prefix: '/compliance',               any: ['audit:read'] },
  { prefix: '/my-dashboard',             any: ['alerts:read'] },
  { prefix: '/investigations',           any: ['cases:read', 'customers:read'] },
  { prefix: '/intelligence/consortium',  any: ['consortium:read'] },
  { prefix: '/customers',                any: ['customers:read'] },
  { prefix: '/transactions',             any: ['transactions:read'] },
  { prefix: '/blocks/approvals',         any: ['blocks:approve'] },
  { prefix: '/blocks/new',               any: ['blocks:request'] },
  { prefix: '/blocks',                   any: ['blocks:read'] },
  { prefix: '/sars',                     any: ['sars:read'] },
  { prefix: '/reports',                  any: ['reports:read'] },
  { prefix: '/handovers',                any: ['handovers:read'] },
  { prefix: '/workflow',                 any: ['workflow:read'] },
  { prefix: '/ml',                       any: ['ml:read'] },
  { prefix: '/dashboard',                any: ['audit:read'] },
  { prefix: '/portfolio',                any: ['audit:read'] },
  { prefix: '/role-dashboards',          any: ['audit:read'] },
  { prefix: '/highlights',               any: ['audit:read'] },
  { prefix: '/operations',               any: ['audit:read'] },
  { prefix: '/analytics',                any: ['audit:read'] },
  // Audit / ops surfaces — split per-page since migration 0102.
  // Mirrors the route guards in router.tsx.
  { prefix: '/admin/audit',              any: ['audit_log:read'] },
  { prefix: '/admin/access-log',         any: ['access_log:read'] },
  { prefix: '/admin/ingestion',          any: ['system:read'] },
  { prefix: '/admin/system-status',      any: ['system:read'] },
  { prefix: '/admin/batch',              any: ['system:read'] },
  { prefix: '/admin/demo-data',          any: ['demo:admin', 'system:admin'] },
  { prefix: '/admin',                    any: ['system:admin'] }, // fallback for /admin/*
]

const SUPER_ADMIN = 'system:admin'

function holdsAny(me: Me | undefined, any: readonly string[]): boolean {
  if (!me) return false
  if (me.permissions.includes(SUPER_ADMIN)) return true
  if (any.length === 0) return true
  return any.some((p) => me.permissions.includes(p))
}

/**
 * pickLandingRoute returns the best route for this `me`. Always
 * returns a string (worst case `/settings`).
 */
export function pickLandingRoute(me: Me | undefined): string {
  for (const rule of LANDING_LADDER) {
    if (holdsAny(me, rule.any)) return rule.to
  }
  // Defensive fallback — shouldn't reach here because the sentinel
  // /settings rule has any=[] which always matches when me is set.
  return '/settings'
}

/**
 * validateNext checks whether the supplied deep-link is allowed for
 * the current `me`. Returns the path itself if it's safe to navigate
 * to, or null when the principal lacks the permission and we should
 * fall back to pickLandingRoute. Rejects external/protocol-relative
 * URLs as well, so a `?next=//evil.com` query can't redirect off-site.
 */
export function validateNext(next: string | null, me: Me | undefined): string | null {
  if (!next) return null
  // Reject anything that isn't an in-app path.
  if (!next.startsWith('/') || next.startsWith('//')) return null
  // Strip query/hash for guard matching but preserve them in the return.
  const pathOnly = next.split(/[?#]/, 1)[0] ?? next
  // Find the most-specific (longest matching prefix) guard.
  let match: { prefix: string; any: readonly string[] } | null = null
  for (const g of PATH_GUARDS) {
    if (pathOnly === g.prefix || pathOnly.startsWith(g.prefix + '/')) {
      if (!match || g.prefix.length > match.prefix.length) match = g
    }
  }
  // Unguarded path (e.g. /settings) — always safe.
  if (!match) return next
  // Permission-gated path — only return when the user holds it.
  if (holdsAny(me, match.any)) return next
  return null
}
